/**
 * GeographyModal CSS Styles
 *
 * CSS-in-JS styles injected once on first component instantiation.
 * Scoped via .sm-geography-modal prefix. Adapted from NeighborhoodModal's
 * styles.ts (same visual language) with amenities/detail-row/favorite/alert/
 * view-map-btn rules dropped — this component doesn't have those sections.
 */
let stylesInjected = false;
const STYLES = `
/* Modal Overlay */
.sm-geography-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s, visibility 0.3s;
}

.sm-geography-overlay.active {
  opacity: 1;
  visibility: visible;
}

/* Modal Container */
.sm-geography-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  height: 85vh;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: translateY(20px);
  transition: transform 0.3s;
  overflow: hidden;
}

.sm-geography-overlay.active .sm-geography-modal {
  transform: translateY(0);
}

/* Fixed Header */
.sm-geography-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: white;
  flex-shrink: 0;
  gap: 16px;
}

.sm-geography-header-left {
  flex: 1;
  min-width: 0;
}

/* Entity-type eyebrow ("Region" / "District") - the level distinguisher
   Todd asked for, without connector lines (see plan discussion). Region is
   bigger/bolder than District so the hierarchy reads at a glance. */
.sm-geography-eyebrow {
  margin: 0 0 2px 0;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.sm-geography-eyebrow.region {
  font-size: 0.75rem;
  color: #2c5530;
}

.sm-geography-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1f2937;
}

.sm-geography-title.region {
  font-size: 1.4rem;
  font-weight: 700;
}

.sm-geography-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.875rem;
  color: #666;
}

.sm-geography-actions {
  margin-top: 8px;
}

/* Alert button - amber-tinted at rest (not just once active) so it reads
   as a distinct, inviting action, and labeled with visible text rather
   than relying on a hover title mobile users never see. Active/subscribed
   state inverts to a solid filled bell - same treatment as
   PropertyModal/NeighborhoodModal's identical alert buttons. */
.sm-geography-alert {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  border-radius: 16px;
  padding: 5px 12px 5px 10px;
  background: #fef3c7;
  color: #d97706;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.sm-geography-alert:hover {
  background: #fde68a;
  color: #b45309;
}

.sm-geography-alert.active {
  background: #f59e0b;
  color: #ffffff;
}

.sm-geography-alert.active:hover {
  background: #d97706;
  color: #ffffff;
}

.sm-geography-alert svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.sm-geography-alert-label {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.sm-geography-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.sm-geography-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f0f0;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sm-geography-close:hover {
  background: #e0e0e0;
}

/* Tab Navigation */
.sm-geography-tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
  padding: 0 20px;
}

.sm-geography-tab {
  padding: 12px 16px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #6b7280;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.sm-geography-tab:hover {
  color: #374151;
}

.sm-geography-tab.active {
  color: #2c5530;
  border-bottom-color: #2c5530;
}

/* Content tab is an agent-only function (see GeographyModalOptions'
   onLoadAgentNote/onSaveAgentNote) - orange instead of the other tabs'
   green so it visibly stands out as an agent addition, not just another
   map tab. Must come after .sm-geography-tab.active to win on specificity
   (both are single-class selectors - source order decides). */
.sm-geography-tab[data-tab="content"].active {
  color: #f97316;
  border-bottom-color: #f97316;
}

/* Modal Body */
.sm-geography-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1 1 0;
  min-height: 0;
}

/* Tab Content */
.sm-geography-tab-content {
  display: none;
}

.sm-geography-tab-content.active {
  display: block;
}

/* Stats Row */
.sm-geography-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.sm-geography-stat {
  text-align: center;
  padding: 12px 8px;
  background: #f9fafb;
  border-radius: 8px;
}

.sm-geography-stat-value {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
}

.sm-geography-stat-label {
  font-size: 0.75rem;
  color: #666;
  margin-top: 2px;
}

/* Market Data tab - current-snapshot headline strip (see GeographyModalOptions'
   onLoadMarketHeadline), shown above the trend chart. Same visual weight as
   the header's .sm-geography-stats row but a distinct class (different
   container/tab) and includes a delta indicator each tile's own stats row
   doesn't have. .ms-tile-delta's up/down/flat classes come from the shared
   delta() helper in MarketStats - reused as-is rather than reimplemented,
   so styled here to match. */
.sm-geography-market-headline {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
}

.sm-geography-market-tile {
  text-align: center;
  padding: 12px 8px;
  background: #f9fafb;
  border-radius: 8px;
}

.sm-geography-market-tile-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #666;
}

.sm-geography-market-tile-value {
  font-size: 1.15rem;
  font-weight: 600;
  color: #333;
  margin-top: 2px;
}

.ms-tile-delta {
  font-size: 0.7rem;
  margin-top: 2px;
}

.ms-tile-delta.up {
  color: #ea580c;
}

.ms-tile-delta.down {
  color: #3b82f6;
}

.ms-tile-delta.flat {
  color: #9ca3af;
}

/* Boundary map */
.sm-geography-map {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
  background: #f0f0f0;
}

.sm-geography-map-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e0f2e9 0%, #c7e5d8 100%);
  color: #2c5530;
  font-size: 0.9rem;
}

/* Description */
.sm-geography-description {
  background: #f9fafb;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.sm-geography-description p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #4b5563;
}

/* Collapsible content sections (Overview/Insights/Market/etc) */
.sm-geography-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.sm-geography-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  background: #f9fafb;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  text-align: left;
  font-family: inherit;
}

.sm-geography-section-header:hover {
  background: #f3f4f6;
}

.sm-geography-section-chevron {
  transition: transform 0.2s;
}

.sm-geography-section.collapsed .sm-geography-section-chevron {
  transform: rotate(-90deg);
}

.sm-geography-section.collapsed .sm-geography-section-content {
  display: none;
}

.sm-geography-section-content {
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
}

/* For Sale Tab */
.sm-geography-listings {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sm-geography-listing-card {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.sm-geography-listing-card:hover {
  background: #f0fdf4;
  border-color: #22c55e;
}

.sm-geography-listing-image {
  width: 100px;
  height: 75px;
  border-radius: 6px;
  object-fit: cover;
  background: #e5e7eb;
  flex-shrink: 0;
}

.sm-geography-listing-image-placeholder {
  width: 100px;
  height: 75px;
  border-radius: 6px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  flex-shrink: 0;
}

.sm-geography-listing-info {
  flex: 1;
  min-width: 0;
}

.sm-geography-listing-price {
  font-weight: 600;
  color: #22c55e;
  margin-bottom: 4px;
  font-size: 1rem;
}

.sm-geography-listing-status-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  vertical-align: middle;
}

.sm-geography-listing-status-badge.pending {
  background: #fef3c7;
  color: #92400e;
}

.sm-geography-listing-address {
  font-size: 0.85rem;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.sm-geography-listing-stats {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #666;
}

.sm-geography-listing-empty {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

/* Sales Data Tab */
.sm-geography-sales-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.sm-geography-sales-stat {
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}

.sm-geography-sales-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
}

.sm-geography-sales-stat-label {
  font-size: 0.8rem;
  color: #666;
  margin-top: 4px;
}

.sm-geography-sales-empty {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

/* Content tab (Agent Insight - agent-only, see GeographyModalOptions'
   onLoadAgentNote/onSaveAgentNote) */
.sm-geography-content-tab-hint {
  font-size: 0.85rem;
  color: #666;
  margin: 0 0 12px;
  line-height: 1.4;
}

.sm-geography-note-status-row {
  margin: 0 0 10px;
  min-height: 20px;
}

.sm-geography-note-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.sm-geography-note-badge-pending { background: #fef3c7; color: #92400e; }
.sm-geography-note-badge-approved { background: #dcfce7; color: #166534; }
.sm-geography-note-badge-rejected { background: #fee2e2; color: #991b1b; }

.sm-geography-note-rejection-reason {
  margin-left: 8px;
  font-size: 0.8rem;
  color: #991b1b;
}

.sm-geography-content-textarea {
  width: 100%;
  min-height: 160px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #1f2937;
  resize: vertical;
  box-sizing: border-box;
}

.sm-geography-content-textarea:focus {
  outline: none;
  border-color: #f97316;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.15);
}

.sm-geography-content-tab-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}

.sm-geography-content-tab-status {
  font-size: 0.8rem;
  color: #666;
}

.sm-geography-content-save-btn {
  padding: 8px 20px;
  background: #f97316;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  font-family: inherit;
}

.sm-geography-content-save-btn:hover {
  background: #ea580c;
}

.sm-geography-content-save-btn:disabled {
  background: #9ca3af;
  cursor: default;
}

/* Loading state */
.sm-geography-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.sm-geography-loading-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #e5e7eb;
  border-top-color: #2c5530;
  border-radius: 50%;
  animation: sm-geography-spin 0.8s linear infinite;
  margin-right: 12px;
}

@keyframes sm-geography-spin {
  to {
    transform: rotate(360deg);
  }
}

/* AI-generated content section prose */
.sm-geography-content-html {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #374151;
}

.sm-geography-content-html p {
  margin: 0 0 1em 0;
}

.sm-geography-content-html p:last-child {
  margin-bottom: 0;
}

.sm-geography-content-html ul,
.sm-geography-content-html ol {
  margin: 0 0 1em 0;
  padding-left: 1.5em;
}

.sm-geography-content-html li {
  margin-bottom: 0.5em;
}

.sm-geography-content-html a {
  color: #2563eb;
  text-decoration: none;
}

.sm-geography-content-html a:hover {
  text-decoration: underline;
}

.sm-geography-content-html h3,
.sm-geography-content-html h4 {
  margin: 1em 0 0.5em 0;
  font-weight: 600;
  color: #1f2937;
}

.sm-geography-content-html h3:first-child,
.sm-geography-content-html h4:first-child {
  margin-top: 0;
}

.sm-geography-agent-insights {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.sm-geography-agent-insights-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
}

.sm-geography-agent-insight {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
}

.sm-geography-agent-insight:last-child {
  margin-bottom: 0;
}

.sm-geography-agent-insight-body {
  font-size: 0.875rem;
  line-height: 1.5;
  color: #374151;
}

.sm-geography-agent-insight-body p {
  margin: 0 0 0.5em 0;
}

.sm-geography-agent-insight-body p:last-child {
  margin-bottom: 0;
}

.sm-geography-agent-insight-byline {
  display: block;
  margin-top: 8px;
  font-size: 0.75rem;
  color: #6b7280;
  text-decoration: none;
}

.sm-geography-agent-insight-byline:hover {
  text-decoration: underline;
  color: #2563eb;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .sm-geography-modal {
    width: 95%;
    height: 95vh;
    max-height: 95vh;
  }

  .sm-geography-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .sm-geography-sales-stats {
    grid-template-columns: 1fr;
  }

  .sm-geography-header {
    padding: 12px 16px;
  }

  .sm-geography-title {
    font-size: 1.2rem;
  }

  .sm-geography-close {
    width: 36px;
    height: 36px;
  }

  .sm-geography-tabs {
    padding: 0 12px;
  }

  .sm-geography-tab {
    padding: 10px 12px;
    font-size: 0.85rem;
  }
}

/* Small mobile: full screen */
@media (max-width: 480px) {
  .sm-geography-modal {
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .sm-geography-tab-content {
    padding: 12px;
  }

  .sm-geography-listing-card {
    flex-direction: column;
  }

  .sm-geography-listing-image,
  .sm-geography-listing-image-placeholder {
    width: 100%;
    height: 140px;
  }
}
`;
export function injectStyles() {
    if (stylesInjected)
        return;
    const styleEl = document.createElement('style');
    styleEl.textContent = STYLES;
    document.head.appendChild(styleEl);
    stylesInjected = true;
}
//# sourceMappingURL=styles.js.map